import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  FiDatabase,
  FiGrid,
  FiChevronRight,
  FiBook,
  FiLayers,
  FiMessageSquare,
  FiFileText,
  FiHeart,
  FiBookOpen,
} from "react-icons/fi";
import {
  fetchLatestSnapshot,
  flattenSnapshotConfig,
} from "../services/snapshotService";
import { getSubjectConfig } from "../utils/subjectConfig";
import { getCurriculumConfig } from "../utils/curriculumConfig";
import { compareGrades } from "../utils/gradeSort";

// Wrapper for backward compatibility - maps new config to old property names
const getCurriculumInfo = (dbName) => {
  const config = getCurriculumConfig(dbName);
  return {
    ...config,
    bgColor: config.cardBgColor, // For cards, use the lighter background
    badgeBg: config.bgColor, // For badges, use the standard bg
  };
};

// Convert curriculum name to URL-friendly slug
const toSlug = (name) => {
  return name.toLowerCase().replace(/\s+/g, "-");
};

const TYPE_SECTIONS = [
  {
    category: "question",
    label: "Questions",
    icon: FiMessageSquare,
    color: "text-blue-600",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-200",
    children: [
      {
        key: "question-simple",
        label: "Simple",
        icon: FiMessageSquare,
        color: "text-blue-500",
        match: (m) =>
          m.request_category === "question" &&
          !m.has_interests &&
          !m.has_stimulus,
      },
      {
        key: "question-stimulus",
        label: "With Stimulus",
        icon: FiBookOpen,
        color: "text-rose-500",
        match: (m) => m.request_category === "question" && m.has_stimulus,
      },
      {
        key: "question-interests",
        label: "With Interests",
        icon: FiHeart,
        color: "text-amber-500",
        match: (m) => m.request_category === "question" && m.has_interests,
      },
    ],
  },
  {
    category: "article",
    label: "Articles",
    icon: FiFileText,
    color: "text-emerald-600",
    bgColor: "bg-emerald-50",
    borderColor: "border-emerald-200",
    children: [
      {
        key: "article-simple",
        label: "Simple",
        icon: FiFileText,
        color: "text-emerald-500",
        match: (m) =>
          m.request_category === "article" &&
          !m.has_interests &&
          !m.has_stimulus,
      },
      {
        key: "article-interests",
        label: "With Interests",
        icon: FiHeart,
        color: "text-amber-500",
        match: (m) => m.request_category === "article" && m.has_interests,
      },
    ],
  },
];

const getTypeQueryParams = (typeKey) => {
  const [category, modifier] = typeKey.split("-");
  const params = new URLSearchParams();
  params.set("category", category);
  if (modifier !== "simple") {
    params.set("modifier", modifier);
  }
  return params.toString();
};

/**
 * Return the typeKey (e.g. "question-simple", "article-interests") for a
 * flat snapshot set entry by matching against TYPE_SECTIONS children.
 */
const getTypeKey = (set) => {
  for (const section of TYPE_SECTIONS) {
    for (const child of section.children) {
      if (child.match(set)) {
        return child.key;
      }
    }
  }
  return null;
};

const BenchmarkSets = () => {
  const [loading, setLoading] = useState(true);

  // Benchmark sets grouped by curriculum > subject > typeKey > [grades]
  const [setsByCurriculum, setSetsByCurriculum] = useState({});
  const [totalSets, setTotalSets] = useState(0);

  // Collapsed state — default all closed (true = collapsed)
  const [collapsedCurricula, setCollapsedCurricula] = useState({});
  const [collapsedTypes, setCollapsedTypes] = useState({});

  const isCurriculumCollapsed = (curriculum) =>
    collapsedCurricula[curriculum] !== false;

  const toggleCurriculum = (curriculum) => {
    setCollapsedCurricula((prev) => ({
      ...prev,
      [curriculum]: prev[curriculum] === false,
    }));
  };

  const isTypeCollapsed = (key) => collapsedTypes[key] !== false;

  const toggleType = (sectionKey) => {
    setCollapsedTypes((prev) => ({
      ...prev,
      [sectionKey]: prev[sectionKey] === false,
    }));
  };

  // Load latest snapshot on mount and expand into grouped structure
  useEffect(() => {
    const loadFromSnapshot = async () => {
      setLoading(true);
      const { data: snapshot, error } = await fetchLatestSnapshot();

      if (snapshot && !error) {
        const flat = flattenSnapshotConfig(snapshot);

        const sorted = [...flat].sort((a, b) => {
          const subjectCompare = a.subject.localeCompare(b.subject);
          if (subjectCompare !== 0) {
            return subjectCompare;
          }
          return compareGrades(a.grade, b.grade);
        });

        // Group by curriculum > subject > typeKey > [grade cards]
        const grouped = {};
        for (const set of sorted) {
          const typeKey = getTypeKey(set);
          if (!typeKey) {
            continue;
          }
          const curriculum = set.curriculum || "unknown";
          if (!grouped[curriculum]) {
            grouped[curriculum] = {};
          }
          const subject = set.subject;
          if (!grouped[curriculum][subject]) {
            grouped[curriculum][subject] = {};
          }
          if (!grouped[curriculum][subject][typeKey]) {
            grouped[curriculum][subject][typeKey] = [];
          }
          grouped[curriculum][subject][typeKey].push(set);
        }

        setSetsByCurriculum(grouped);
        setTotalSets(sorted.length);
      } else {
        console.error("Error loading dimension configs:", error);
      }
      setLoading(false);
    };

    loadFromSnapshot();
  }, []);

  // Count unique subjects across all curricula
  const uniqueSubjects = new Set();
  Object.values(setsByCurriculum).forEach((subjects) => {
    Object.keys(subjects).forEach((subject) => uniqueSubjects.add(subject));
  });

  // Get curriculum abbreviations for stats display
  const curriculumAbbreviations = Object.keys(setsByCurriculum)
    .map((c) => getCurriculumInfo(c).abbreviation)
    .join(", ");

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-10">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="mb-2 flex items-center gap-3">
                <div className="flex size-10 items-center justify-center rounded-lg bg-primary-100">
                  <FiDatabase className="size-5 text-primary-600" />
                </div>
                <h1 className="text-4xl font-semibold text-gray-900">
                  Benchmark Sets
                </h1>
              </div>
              <p className="mt-3 max-w-2xl text-lg text-gray-600">
                Explore benchmark configurations across different curricula,
                subjects, and grades.
              </p>
            </div>
          </div>
        </div>

        {/* Stats Summary */}
        {!loading && (
          <div className="mb-8 grid gap-4 sm:grid-cols-3">
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="flex size-10 items-center justify-center rounded-lg bg-primary-50">
                  <FiGrid className="size-5 text-primary-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {totalSets}
                  </p>
                  <p className="text-sm text-gray-500">Total Benchmark Sets</p>
                </div>
              </div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="flex size-10 items-center justify-center rounded-lg bg-green-50">
                  <FiBook className="size-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {uniqueSubjects.size}
                  </p>
                  <p className="text-sm text-gray-500">Subjects Available</p>
                </div>
              </div>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="flex size-10 items-center justify-center rounded-lg bg-violet-50">
                  <FiLayers className="size-5 text-violet-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    {Object.keys(setsByCurriculum).length}{" "}
                    {Object.keys(setsByCurriculum).length === 1
                      ? "Curriculum"
                      : "Curricula"}
                  </p>
                  <p className="text-sm text-gray-500">
                    {curriculumAbbreviations || "\u2014"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="mx-auto mb-4 size-12 animate-spin rounded-full border-y-2 border-primary-600"></div>
                <p className="text-sm text-gray-500">
                  Loading benchmark sets...
                </p>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Grouped by Curriculum */}
            {Object.entries(setsByCurriculum).map(
              ([curriculum, subjectGroups]) => {
                const currInfo = getCurriculumInfo(curriculum);
                const isCollapsed = isCurriculumCollapsed(curriculum);
                const totalSetsInCurriculum = Object.values(
                  subjectGroups
                ).reduce(
                  (sum, typeGroups) =>
                    sum +
                    Object.values(typeGroups).reduce(
                      (s, sets) => s + sets.length,
                      0
                    ),
                  0
                );

                return (
                  <div key={curriculum} className="mb-8">
                    {/* Curriculum Header */}
                    <button
                      onClick={() => toggleCurriculum(curriculum)}
                      className={`mb-4 flex w-full items-center justify-between rounded-xl border ${currInfo.borderColor} ${currInfo.bgColor} p-4 transition-all hover:shadow-md`}
                    >
                      <div className="flex items-center gap-3">
                        <span
                          className={`rounded-lg ${currInfo.badgeBg} px-3 py-1.5 text-base font-bold ${currInfo.color}`}
                        >
                          {currInfo.abbreviation}
                        </span>
                        <div className="text-left">
                          <h2 className="text-lg font-semibold text-gray-900">
                            {currInfo.fullName}
                          </h2>
                          <p className="text-sm text-gray-500">
                            {totalSetsInCurriculum} benchmark sets •{" "}
                            {Object.keys(subjectGroups).length} subjects
                          </p>
                        </div>
                      </div>
                      <FiChevronRight
                        className={`size-5 text-gray-400 transition-transform ${
                          isCollapsed ? "" : "rotate-90"
                        }`}
                      />
                    </button>

                    {/* Collapsible Content */}
                    {!isCollapsed && (
                      <div className="ml-2">
                        {Object.entries(subjectGroups).map(
                          ([subject, typeGroups]) => {
                            const subjectConfig = getSubjectConfig(
                              subject.toLowerCase()
                            );
                            const SubjectIcon = subjectConfig.icon;
                            const allGrades = Object.values(typeGroups)
                              .flat()
                              .map((s) => s.grade);
                            const uniqueGrades = [...new Set(allGrades)];
                            const gradeRange =
                              uniqueGrades.length > 1
                                ? `${uniqueGrades[0]}\u2013${uniqueGrades[uniqueGrades.length - 1]}`
                                : uniqueGrades[0];
                            const totalTypeCount = Object.values(
                              typeGroups
                            ).reduce((s, arr) => s + arr.length, 0);

                            return (
                              <div
                                key={`${curriculum}-${subject}`}
                                className="mb-6 ml-2"
                              >
                                {/* Subject Header */}
                                <div className="mb-3 flex items-center gap-3">
                                  <div
                                    className={`flex size-8 items-center justify-center rounded-lg ${subjectConfig.bgColor}`}
                                  >
                                    <SubjectIcon
                                      className={`size-4 ${subjectConfig.color}`}
                                    />
                                  </div>
                                  <div>
                                    <h3 className="text-base font-semibold text-gray-900">
                                      {subject}
                                    </h3>
                                    <p className="text-xs text-gray-500">
                                      {totalTypeCount} sets •{" "}
                                      {currInfo.gradeLabel || "Grade"}s{" "}
                                      {gradeRange}
                                    </p>
                                  </div>
                                </div>

                                {/* Type Sections */}
                                <div className="ml-2 space-y-3">
                                  {TYPE_SECTIONS.map((parentType) => {
                                    const childrenWithData =
                                      parentType.children.filter(
                                        (child) => typeGroups[child.key]
                                      );
                                    if (childrenWithData.length === 0) {
                                      return null;
                                    }

                                    const ParentIcon = parentType.icon;
                                    const parentKey = `${curriculum}-${subject}-${parentType.category}`;
                                    const isParentCollapsed =
                                      isTypeCollapsed(parentKey);

                                    return (
                                      <div key={parentKey}>
                                        <button
                                          onClick={() => toggleType(parentKey)}
                                          className={`mb-2 flex w-full items-center gap-3 rounded-lg border ${parentType.borderColor} ${parentType.bgColor} px-4 py-3 text-left transition-all hover:shadow-sm`}
                                        >
                                          <FiChevronRight
                                            className={`size-4 text-gray-400 transition-transform ${
                                              isParentCollapsed
                                                ? ""
                                                : "rotate-90"
                                            }`}
                                          />
                                          <ParentIcon
                                            className={`size-5 ${parentType.color}`}
                                          />
                                          <span
                                            className={`text-base font-semibold ${parentType.color}`}
                                          >
                                            {parentType.label}
                                          </span>
                                          <span className="text-sm text-gray-400">
                                            (
                                            {childrenWithData.reduce(
                                              (s, c) =>
                                                s + typeGroups[c.key].length,
                                              0
                                            )}{" "}
                                            sets)
                                          </span>
                                        </button>

                                        {!isParentCollapsed && (
                                          <div className="ml-4 space-y-2">
                                            {childrenWithData.map((child) => {
                                              const sets =
                                                typeGroups[child.key];
                                              const ChildIcon = child.icon;
                                              const childKey = `${parentKey}-${child.key}`;
                                              const isChildCollapsed =
                                                isTypeCollapsed(childKey);

                                              return (
                                                <div
                                                  key={child.key}
                                                  className="ml-2"
                                                >
                                                  <button
                                                    onClick={() =>
                                                      toggleType(childKey)
                                                    }
                                                    className="mb-2 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-gray-50"
                                                  >
                                                    <FiChevronRight
                                                      className={`size-3.5 text-gray-400 transition-transform ${isChildCollapsed ? "" : "rotate-90"}`}
                                                    />
                                                    <ChildIcon
                                                      className={`size-4 ${child.color}`}
                                                    />
                                                    <span className="text-sm font-semibold text-gray-700">
                                                      {child.label}
                                                    </span>
                                                    <span className="text-xs font-medium text-gray-400">
                                                      ({sets.length})
                                                    </span>
                                                  </button>

                                                  {/* Grade Cards Grid */}
                                                  {!isChildCollapsed && (
                                                    <div className="mb-3 ml-2 grid gap-2.5 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-7 xl:grid-cols-9">
                                                      {sets.map(
                                                        (set, index) => {
                                                          // Display the grade verbatim — DB stores the
                                                          // full curriculum-repo course name (e.g.
                                                          // "Kindergarten", "1st Grade",
                                                          // "5th Grade: US History (1491-1850)").
                                                          const gradeDisplay =
                                                            set.grade;

                                                          return (
                                                            <Link
                                                              key={`${child.key}-${set.grade}-${index}`}
                                                              to={`/benchmark-sets/${toSlug(curriculum)}/${encodeURIComponent(set.grade)}/${encodeURIComponent(set.subject)}?${getTypeQueryParams(child.key)}`}
                                                              className={`group relative flex flex-col items-center overflow-hidden rounded-xl border-2 ${subjectConfig.borderColor} bg-white p-3 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md`}
                                                            >
                                                              <div
                                                                className={`absolute right-0 top-0 size-12 -translate-y-4 translate-x-4 rounded-full ${subjectConfig.bgColor} opacity-40 transition-all duration-200 group-hover:scale-150 group-hover:opacity-60`}
                                                              ></div>

                                                              <div
                                                                className={`relative z-10 mb-1.5 flex min-h-9 min-w-9 max-w-full items-center justify-center rounded-full bg-gradient-to-br px-2.5 py-1 ${subjectConfig.gradientFrom} ${subjectConfig.gradientTo} transition-transform duration-200 group-hover:scale-110`}
                                                              >
                                                                <span
                                                                  className={`break-words text-center text-xs font-bold leading-tight ${subjectConfig.color}`}
                                                                >
                                                                  {gradeDisplay}
                                                                </span>
                                                              </div>

                                                              <h3 className="relative z-10 text-center text-xs font-semibold text-gray-900">
                                                                {subject}
                                                              </h3>

                                                              <div
                                                                className={`relative z-10 mt-1 rounded-md ${subjectConfig.bgColor} px-1.5 py-0.5`}
                                                              >
                                                                <span
                                                                  className={`text-[10px] font-medium ${subjectConfig.color}`}
                                                                >
                                                                  {
                                                                    currInfo.abbreviation
                                                                  }
                                                                </span>
                                                              </div>
                                                            </Link>
                                                          );
                                                        }
                                                      )}
                                                    </div>
                                                  )}
                                                </div>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          }
                        )}
                      </div>
                    )}
                  </div>
                );
              }
            )}

            {/* Empty State */}
            {Object.keys(setsByCurriculum).length === 0 && (
              <div className="rounded-xl border-2 border-dashed border-gray-300 bg-white p-12 text-center">
                <FiDatabase className="mx-auto size-12 text-gray-400" />
                <h3 className="mt-4 text-lg font-medium text-gray-900">
                  No benchmark sets configured
                </h3>
                <p className="mt-2 text-gray-500">
                  Add dimension configs to the bm_dimension_config table to see
                  benchmark sets here.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BenchmarkSets;
